import React from "react";
import Button from "@amzn/meridian/esm/components/button";
import Coachmark from "@amzn/meridian/esm/components/coachmark";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Icon from "@amzn/meridian/esm/components/icon";
import Loader from "@amzn/meridian/esm/components/loader";
import Popover from "@amzn/meridian/esm/components/popover";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Toggle from "@amzn/meridian/esm/components/toggle";
import chevronRightTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";

import { BUILT_IN_AGENT } from "../../constants";
import { THEME_COLOR } from "../../styles/colors";
import { truncateDisplayName } from "../../utils/agentDisplayUtils";

const TOGGLE_THEME = {
  toggleTrackBackgroundColorSelectedDefault: THEME_COLOR.TEAL_GREEN,
  toggleTrackBackgroundColorSelectedHover: THEME_COLOR.TEAL_GREEN,
};

const COACHMARK_THEME = {
  coachmarkMarkerBackgroundColorDefault: THEME_COLOR.CORAL_RED,
  coachmarkMarkerBackgroundColorHover: THEME_COLOR.CORAL_RED,
  coachmarkMarkerBackgroundColorPressed: THEME_COLOR.CORAL_RED,
  coachmarkOrbBorderColor: THEME_COLOR.CORAL_RED,
  coachmarkOrbSizeStart: 8,
  coachmarkOrbSizeEnd: 20,
  coachmarkMarkerSize: 8,
  coachmarkMarkerSpacingInset: 0,
};

/**
 * SettingsPopover - Popover content for in-session settings (send-to-all toggle, meeting agents).
 * Rendered for all participants during an active session.
 */
const SettingsPopover = ({
  anchorNode,
  open,
  onClose,
  onOpenGeneral,
  showSendToAll,
  sendToAllAttendees,
  onSendToAllAttendeesChange,
  showAgentToggle = false,
  canToggleAgents = false,
  enabledAgentIds,
  availableExtensions = [],
  onToggleAgent,
  agentsLoading = false,
}) => {
  const handleOpenGeneral = () => {
    onClose();
    if (onOpenGeneral) onOpenGeneral();
  };

  return (
    <Popover
      anchorNode={anchorNode}
      open={open}
      onClose={onClose}
      position="top"
      alignment="end"
      spacingInset="none"
      indicatorArrow={false}
    >
      <Column spacing="none" width="300px">
        {showSendToAll && (
          <>
            <Column spacing="none" spacingInset="400 400 200 400">
              <Text type="h50">Summary for this meeting</Text>
            </Column>
            <Theme tokens={{ buttonHeightMedium: 56 }}>
              <Button type="link" onClick={() => onSendToAllAttendeesChange(!sendToAllAttendees)} minWidth="300px">
                <Row widths={["fill", "fit"]} alignmentVertical="center" width="268px" spacingInset="none">
                  <Column spacing="none">
                    <Text type="b200">Send meeting summary to all</Text>
                    <Text type="b200">attendees</Text>
                  </Column>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Theme tokens={TOGGLE_THEME}>
                      <Toggle
                        checked={sendToAllAttendees}
                        onChange={() => onSendToAllAttendeesChange(!sendToAllAttendees)}
                        size="small"
                      />
                    </Theme>
                  </div>
                </Row>
              </Button>
            </Theme>
            <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}><Divider spacingBefore="300" /></Theme>
          </>
        )}

        {/* Meeting agents section: visible to all, toggles only for organizer */}
        {showAgentToggle && (
          <>
            <Column spacing="none" spacingInset="400 400 200 400">
              <Text type="h50">Meeting agents</Text>
            </Column>
            <Column spacing="none">
              {/* Built-in agent — always on, no toggle */}
              <Button type="link" minWidth="300px">
                <Row widths={["fill", "fit"]} alignmentVertical="center" width="268px" spacingInset="200 none">
                  <Row spacing="300" alignmentVertical="center">
                    <Text type="b200">{BUILT_IN_AGENT.name}</Text>
                    <Theme tokens={COACHMARK_THEME}>
                      <div style={{ pointerEvents: "none", display: "flex", alignItems: "center", height: "100%" }}>
                        <Coachmark />
                      </div>
                    </Theme>
                  </Row>
                </Row>
              </Button>

              {/* Extension agents from API */}
              {availableExtensions.map((ext) => {
                const enabled = enabledAgentIds.includes(ext.extensionId);
                const name = truncateDisplayName(ext.displayName || ext.defaultDisplayName);
                return (
                  <Button
                    key={ext.extensionId}
                    type="link"
                    minWidth="300px"
                    onClick={canToggleAgents ? () => onToggleAgent(ext.extensionId, !enabled) : undefined}
                  >
                    <Row widths={["fill", "fit"]} alignmentVertical="center" width="268px" spacingInset="200 none">
                      <Row spacing="300" alignmentVertical="center">
                        <Text type="b200">{name}</Text>
                        {enabled && (
                          <Theme tokens={COACHMARK_THEME}>
                            <div style={{ pointerEvents: "none", display: "flex", alignItems: "center", height: "100%" }}>
                              <Coachmark />
                            </div>
                          </Theme>
                        )}
                      </Row>
                      {canToggleAgents && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Theme tokens={TOGGLE_THEME}>
                            <Toggle
                              checked={enabled}
                              onChange={() => onToggleAgent(ext.extensionId, !enabled)}
                              size="small"
                            />
                          </Theme>
                        </div>
                      )}
                    </Row>
                  </Button>
                );
              })}
              {agentsLoading && (
                <Row alignmentHorizontal="center" spacingInset="300">
                  <Loader size="small" />
                </Row>
              )}
            </Column>
            <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}><Divider spacingBefore="300" /></Theme>
          </>
        )}

        <Theme tokens={{ buttonHeightMedium: 48 }}>
          <Button type="link" onClick={handleOpenGeneral} minWidth="300px">
            <Row widths={["fill", "fit"]} alignmentVertical="center" width="300px">
              <Text type="h50">General</Text>
              <Icon tokens={chevronRightTokens} color="primary" />
            </Row>
          </Button>
        </Theme>
      </Column>
    </Popover>
  );
};

export default SettingsPopover;
